<script lang="ts" setup>
import { computed, nextTick, ref, watch } from 'vue'
import { theme as brandTheme, resources } from '@/config/brands'

const props = defineProps({
  tagsList: {
    type: Array as () => Array<{ id: string; isHot: boolean; tagName: string }>,
    required: true,
    default: () => [],
  },
  activeTab: {
    type: Number,
    required: true,
    default: 0,
  },
})

const emit = defineEmits(['update:active-tab', 'toggle-all-tags'])

const popupTagBorderColor = computed(() => brandTheme.components?.tags?.inactiveBorder || 'rgba(255, 255, 255, 0.06)')
const iconColor = computed(() => brandTheme.components?.tags?.iconColor || 'rgba(248, 247, 245, 0.42)')
const hotIconStyle = {
  maskImage: `url(${resources?.tagsIcons?.hotIconRed || '/images/flipnovel/tags/hot-flame.svg'})`,
  WebkitMaskImage: `url(${resources?.tagsIcons?.hotIconRed || '/images/flipnovel/tags/hot-flame.svg'})`,
}
const toggleIconStyle = computed(() => ({
  maskImage: `url(${resources?.tagsIcons?.slicesTags})`,
  WebkitMaskImage: `url(${resources?.tagsIcons?.slicesTags})`,
}))
const foldIconStyle = computed(() => ({
  maskImage: `url(${resources?.tagsIcons?.slicesFold})`,
  WebkitMaskImage: `url(${resources?.tagsIcons?.slicesFold})`,
}))

const showAllTags = ref(false)
const scrollContainer = ref<HTMLElement | null>(null)

function disableScroll() {
  const tagsContainer = document.querySelector('#tagsContainer') as HTMLElement
  tagsContainer.style.position = 'fixed'
  tagsContainer.style.width = '100%'
}

function enableScroll() {
  const tagsContainer = document.querySelector('#tagsContainer') as HTMLElement
  tagsContainer.style.position = ''
  tagsContainer.style.top = ''
  tagsContainer.style.width = ''
}

watch(
  () => showAllTags.value,
  (val) => {
    if (val)
      disableScroll()
    else
      enableScroll()
  },
)

function toggleAllTags(e?: Event) {
  e?.stopPropagation()
  showAllTags.value = !showAllTags.value
  emit('toggle-all-tags', showAllTags.value)
}

async function handleTagClick(index: number, e?: Event) {
  if (props.activeTab === index)
    return
  if (showAllTags.value)
    showAllTags.value = false
  emit('update:active-tab', index)
  await nextTick(scrollToActiveTag)
}

watch(
  () => props.activeTab,
  () => {
    nextTick(scrollToActiveTag)
  },
  { immediate: true },
)

function scrollToActiveTag() {
  if (!scrollContainer.value)
    return

  const buttons = scrollContainer.value.querySelectorAll('button')
  if (buttons.length > props.activeTab) {
    buttons[props.activeTab].scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    })
  }
}
</script>

<template>
  <div class="sticky top-0 pl-20px pr-36px pt-8px pb-6px z-30 bg-transparent">
    <div class="relative">
      <div ref="scrollContainer" class="flex overflow-x-auto hide-scrollbar">
        <div class="flex flex-nowrap">
          <button
            v-for="(tab, index) in tagsList" :key="tab.id || index"
            class="flex-shrink-0 px-3 py-1 text-15px select-none" :class="{
              'text-n-primary font-bold': activeTab === index,
              'text-n-text-3': activeTab !== index,
            }" @click="handleTagClick(index, $event)"
          >
            <span class="inline-flex items-center">
              <span v-if="tab.isHot" class="rr-hot-icon mr-1 flex-shrink-0" :style="hotIconStyle" aria-hidden="true" />
              <span class="whitespace-nowrap">{{ tab.tagName }}</span>
            </span>
            <div v-if="activeTab === index" class="mx-auto mt-1 h-3px w-6 rounded-full bg-n-primary" />
          </button>
        </div>
      </div>

      <div class="absolute -right-16px top-0 bottom-0 flex items-center z-10">
        <button class="pointer-events-auto select-none" @click="toggleAllTags($event)">
          <span class="rr-toggle-icon" :style="toggleIconStyle" />
        </button>
      </div>

      <transition name="panel">
        <div v-if="showAllTags" class="fixed inset-0 z-40" @click.self="toggleAllTags">
          <div class="absolute left-0 right-0 top-89px bottom-0 bg-black bg-opacity-50 pointer-events-none" />
          <div class="absolute left-0 right-0 top-88px bg-n-bg-global max-h-[50vh] overflow-y-auto" @click.stop>
            <div class="flex flex-wrap px-5">
              <button
                v-for="(tag, index) in tagsList" :key="tag.id || index"
                class="popup-tag mx-4px my-2.25 px-3 h-28px text-10px rounded-full whitespace-nowrap flex items-center justify-center select-none"
                :class="[
                  activeTab === index
                    ? 'popup-tag--active bg-n-primary text-white font-medium'
                    : 'popup-tag--inactive bg-n-bg-tag text-n-text-2 border border-n-border-primary',
                ]" @click="handleTagClick(index, $event)"
              >
                <span v-if="tag.isHot" class="rr-hot-icon rr-hot-icon--sm mr-1" :style="hotIconStyle" aria-hidden="true" />
                {{ tag.tagName }}
              </button>
            </div>
            <div class="sticky bottom-0 left-0 right-0 bg-n-bg-global py-3 flex justify-center">
              <button class="flex items-center justify-center w-3 h-3 select-none" @click="toggleAllTags($event)">
                <span class="rr-fold-icon" :style="foldIconStyle" aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<style scoped>
.hide-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.hide-scrollbar::-webkit-scrollbar {
  display: none;
}

.panel-leave-active {
  transition: all 0.3s ease;
}

.panel-enter-from,
.panel-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

button {
  cursor: pointer;
  user-select: none;
}

.popup-tag--inactive {
}

.rr-hot-icon {
  display: inline-block;
  width: 12px;
  height: 12px;
  background-color: currentColor;
  mask-repeat: no-repeat;
  -webkit-mask-repeat: no-repeat;
  mask-position: center;
  -webkit-mask-position: center;
  mask-size: contain;
  -webkit-mask-size: contain;
}

.rr-hot-icon--sm {
  width: 11px;
  height: 11px;
}

.rr-toggle-icon,
.rr-fold-icon {
  display: block;
  background-color: v-bind(iconColor);
  mask-repeat: no-repeat;
  -webkit-mask-repeat: no-repeat;
  mask-position: center;
  -webkit-mask-position: center;
  mask-size: contain;
  -webkit-mask-size: contain;
}

.rr-toggle-icon {
  width: 16px;
  height: 16px;
}

.rr-fold-icon {
  width: 12px;
  height: 12px;
}
</style>
